import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { safeDivide } from 'helpers/utilities';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { reduceLayerTwoData } from 'helpers/data';
import { validatePercentagesData } from 'helpers/validate';
import RenderingTile from 'components/molecules/RenderingTile';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TreemapChart from 'components/molecules/charts/TreemapChart';
import SocialAdSetOverviewPivotTable from 'components/molecules/tables/SocialAdSetOverviewPivotTable';
import SocialActionOverviewTable from 'components/molecules/tables/SocialActionOverviewTable';
import SocialConversionsChart from 'components/molecules/charts/SocialConversionsChart';
import TileWithValidation from 'components/molecules/TileWithValidation';
import { LAYERTHREE, LAYERTWO } from 'constants/layers';
// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

class ConversionsExpandModule extends Component {
    render() {
        const {
            backfillBuckets,
            xAxisLabel,
            layer = LAYERTHREE,
            layerTwo,
            showConversionRevenue,
            entityMeta,
            entityObjective,
            entityObjectiveLabelCode
        } = this.props;
        const {
            conversionTermSingular,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const { adsets, actions, timeData, campaigns } = this.props.module;
        const stub = `l${layer}-social`;
        const totalConversions = get(adsets, 'statistics.conversions', 0);
        const hasAdSetData = get(adsets, 'hasData', null);
        const l2Data = get(layerTwo, 'social', {});
        const hasLayerTwoSummaryData = get(l2Data, 'summary.hasData', null);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');
        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(l2Data.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }
        const rankedConversions = hasAdSetData ? adsets.rankings.conversions.reduce((prev, data) => {
            const adSetName = adsets.adSetIdMap[data.name].name;
            const statistics = adsets[data.name].statistics;
            prev.data.push(statistics);
            prev.categories.push(adSetName);

            if (statistics.conversions > 0) {
                prev.percentages.push({
                    name: adSetName,
                    value: safeDivide(statistics.conversions, prev.total)
                });
            }

            return prev;
        }, { categories: [], data: [], total: totalConversions, percentages: [] }) : {};

        const { data } = timeData;
        const hasTimeData = get(timeData, 'hasData', null);
        const objectives = get(entityMeta, 'social.objectives', []);
        const showLayerTwoSummaryTable = !!(hasLayerTwoSummaryData && (layer === LAYERTWO) && (objectives.length > 1));
        const showCampaignSummaryTable = !!(hasAdSetData && layer === LAYERTHREE);
        const userId = get(this.props, 'currentUser.id', null);
        const entityId = get(this.props, 'entityId', null);

        const layerTwoSummaryAccessors = ['impressions', 'clicks', 'cpc', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversion_rate', 'engagements', 'engagement_rate', 'spend'];
        return (

            <React.Fragment>
                {hasAdSetData ? (
                    <TwoAxisBarWithScatterChart
                        title="CONVERSIONS VS. COST / CONVERSION"
                        exportName={`${stub}-conversions-conversions-vs-cost-per-converion`}
                        data={rankedConversions.data}
                        yBar="conversions"
                        scatter="cost_per_conversion"
                        scatterTitle="Cost / Conversion"
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                        animateDuration={2000}
                        categories={rankedConversions.categories} />
                ) : (
                    <RenderingTile height='400px' show={hasAdSetData} />
                )}

                {hasTimeData ? (
                    <TwoAxisBarWithLineChart
                        title="Impressions VS. Conversion Rate"
                        exportName={`${stub}-conversions-impressions-vs-conversion-rate`}
                        data={data}
                        yBar="impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine="conversion_rate"
                        yLineTitle="Conversion Rate"
                        yLineOptions={{ formatByType: 'percentage' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasTimeData} />
                )}

                <TileWithValidation
                    hasData={hasAdSetData}
                    innerComponent={TreemapChart}
                    innerProps={{
                        title: 'Conversion Overview',
                        exportName: `${stub}-conversions-overview`,
                        formatByType: 'percentage',
                        data: rankedConversions.percentages,
                        validation: {
                            key: 'percentages',
                            data: rankedConversions,
                            validator: validatePercentagesData
                        }
                    }}
                />

                <SocialConversionsChart actions={actions} exportNamePrefix={`${stub}-conversions`} />

                {showCampaignSummaryTable ? (
                    <SocialAdSetOverviewPivotTable
                        title="Campaign Overview"
                        showConversionRevenue={showConversionRevenue}
                        exportName={`${stub}-conversions-summary-adsets-overview`}
                        adSets={adsets} />
                ) : (
                    <RenderingTile height='400px' show={showCampaignSummaryTable} />
                )}

                {showLayerTwoSummaryTable ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={'social'}
                        showConversionRevenue={showConversionRevenue}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        exportName='l2-social-conversions-table-performance'
                        accessors={layerTwoSummaryAccessors}
                        cacheKey={`l2-social-conversions-table-performance-${userId}-${entityId}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showLayerTwoSummaryTable} />
                )}

                <SocialActionOverviewTable
                    exportName={`${stub}-conversions-action-overview`}
                    actions={actions}
                    conversionTermSingular={conversionTermSingular}
                    campaigns={campaigns}
                    cacheKey={`${stub}-conversions-action-overview-${userId}-${entityId}`}
                />
            </React.Fragment>
        );
    }
}

ConversionsExpandModule.propTypes = {
    module: PropTypes.shape({
        actions: PropTypes.object,
        adsets: PropTypes.object,
        campaigns: PropTypes.object,
        timeData: PropTypes.object,
        showSemCpconv: PropTypes.string,
        showConversionRevenue: PropTypes.bool,
        entityObjective: PropTypes.string,
        entityObjectiveLabelCode: PropTypes.string,
    }).isRequired
};

export default ConversionsExpandModule;



// WEBPACK FOOTER //
// ./src/components/organisms/social/ConversionsExpandModule.js